import { Alert, Button, Checkbox } from "@digdir/designsystemet-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams, useRouter } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { httpClient } from "@/api/client";
import type { GetAvtaleSelskapResponse } from "@/api/generated";
import {
  getAvtalerQueryKey,
  getAvtaleSelskapOptions,
  getAvtaleSelskapQueryKey,
  postAvbestillAvtaleMutation,
} from "@/api/generated/@tanstack/react-query.gen";
import { DescriptionList, DescriptionTerm, Page } from "@/components";
import { useToaster } from "@/features/toaster";
import { formatOrgNr, formatPercentage } from "@/utils";
import {
  dateHasPassed,
  formatDateString,
  formatDateStringInterval,
} from "@/utils/dateTimeUtils";
import { AvtaleSectionWithHeader } from "../common/AvtaleSectionWithHeader";
import { AvbestillBekreftelseManglerFeil } from "./AvbestillBekreftelseManglerFeil";
import { AvbestillDetaljerContainer } from "./AvbestillDetaljerContainer";
import { AvbestillFeilet } from "./AvbestillFeilet";
import { AvbestillHeader } from "./AvbestillHeader";
import { AvbestillingsFristUtloptAlert } from "./AvbestillingsFristUtloptAlert";
import { AvbestillLoadingSkeleton } from "./AvbestillLoadingSkeleton";
import { AvtaleAvbestiltAlert } from "./AvtaleAvbestiltAlert";
import { AvtaleUtloptAlert } from "./AvtaleUtloptAlert";

export const AvbestillSelskapsAvtalePage = () => {
  const { t } = useTranslation();
  const params = useParams({
    from: "/_app/avtaler/$avtalerId/selskap/avbestill/",
  });

  const {
    data: selskapsAvtale,
    isLoading: isLoadingSelskapsAvtale,
    isError: isErrorSelskapsAvtale,
  } = useQuery({
    ...getAvtaleSelskapOptions({
      client: httpClient,
      path: {
        id: params.avtalerId,
      },
    }),
  });

  return (
    <Page withBackLink>
      <div className="flex flex-col gap-6 max-w-2xl">
        <AvbestillHeader />

        {isLoadingSelskapsAvtale ? (
          <AvbestillLoadingSkeleton />
        ) : isErrorSelskapsAvtale ? (
          <Alert data-color={"danger"}>
            {t("pages.avtaler.avbestilling.feil-lasting")}
          </Alert>
        ) : (
          <SelskapsAvtaleDetaljer selskapsAvtale={selskapsAvtale} />
        )}
      </div>
    </Page>
  );
};

const SelskapsAvtaleDetaljer = ({
  selskapsAvtale,
}: {
  selskapsAvtale: GetAvtaleSelskapResponse | undefined;
}) => {
  const { t } = useTranslation();
  const router = useRouter();
  const toast = useToaster();
  const queryClient = useQueryClient();

  const postAvbestillMutation = useMutation({
    ...postAvbestillAvtaleMutation({
      client: httpClient,
    }),
    onError: (_, __, ___) => {
      setAvbestillingFeilet(true);
    },
    onSuccess: (response, options, __) => {
      queryClient.invalidateQueries({
        queryKey: getAvtaleSelskapQueryKey({
          path: { id: options.path.id },
          client: httpClient,
        }),
      });
      queryClient.invalidateQueries({
        queryKey: getAvtalerQueryKey({
          client: httpClient,
        }),
      });
      const footer =
        response.avtale.cancelledAt != null &&
        formatDateString(response.avtale.cancelledAt);
      toast.success({
        messageKey: "pages.avtaler.avbestilling.alert-vellykket-beskrivelse",
        titleKey: "pages.avtaler.avbestilling.alert-vellykket-tittel",
        footer,
      });

      router.navigate({ to: "/avtaler" });
    },
  });

  const [bekreftAvbestilling, setBekreftAvbestilling] = useState(false);
  const [avbestillingBekreftet, setAvbestillingBekreftet] = useState(false);
  const [avbestillingFeilet, setAvbestillingFeilet] = useState(false);

  const handleAvbestill = () => {
    setAvbestillingFeilet(false);
    if (bekreftAvbestilling !== true) {
      setAvbestillingBekreftet(true);
      return;
    }

    setAvbestillingBekreftet(false);

    const avtaleId = selskapsAvtale?.avtale.avtaleId;
    if (avtaleId == null) return;
    postAvbestillMutation.mutate({ body: {}, path: { id: avtaleId } });
  };

  const handleAvbryt = () => {
    router.navigate({ to: "/" });
  };

  return (
    <AvbestillDetaljerContainer>
      <AvtaleSectionWithHeader
        title={t("pages.avtaler.avbestilling.leverandør-tittel")}
      >
        <DescriptionList>
          <DescriptionTerm
            tranlsationFeltKey="pages.avtaler.avbestilling.leverandør-navn"
            value={selskapsAvtale?.avtale.leverandør.name}
          />
        </DescriptionList>
      </AvtaleSectionWithHeader>
      <AvtaleSectionWithHeader
        title={t("pages.avtaler.avbestilling.bestiller-tittel")}
      >
        <DescriptionList>
          <DescriptionTerm
            tranlsationFeltKey="pages.avtaler.avtaler.detaljer.bestiller-navn"
            value={selskapsAvtale?.avtale.avtaleEier.navn}
          />
          <DescriptionTerm
            tranlsationFeltKey="pages.avtaler.avtaler.detaljer.organisasjonsNummer"
            value={formatOrgNr(
              selskapsAvtale?.avtale.avtaleEier.organisasjonsnummer,
            )}
          />
          <DescriptionTerm
            tranlsationFeltKey="pages.avtaler.avbestilling.innsender"
            value={selskapsAvtale?.avtale.bestiller.navn}
          />
        </DescriptionList>
      </AvtaleSectionWithHeader>
      <AvtaleSectionWithHeader
        title={t("pages.avtaler.avbestilling.avtaleperiode-tittel")}
      >
        <DescriptionList>
          <DescriptionTerm
            tranlsationFeltKey="pages.avtaler.avbestilling.avtaleperiode-dato"
            value={`${formatDateStringInterval(selskapsAvtale?.avtale.activeFromInclusive, selskapsAvtale?.avtale.activeToExclusive)}`}
          />
        </DescriptionList>
      </AvtaleSectionWithHeader>
      <AvtaleSectionWithHeader
        title={t("pages.avtaler.avbestilling.informasjon-selskap-tittel")}
      >
        <DescriptionList>
          <DescriptionTerm
            tranlsationFeltKey="pages.avtaler.avtaler.detaljer.organisasjonType"
            value={t("enums.boligType", {
              context: selskapsAvtale?.avtale.avtaleDetaljerSelskap?.boligType,
            })}
          />
          <DescriptionTerm
            tranlsationFeltKey="pages.avtaler.avtaler.detaljer.kundeNummer"
            value={selskapsAvtale?.avtale.kundenummer}
          />
          <DescriptionTerm
            tranlsationFeltKey="pages.avtaler.avtaler.detaljer.målerId"
            value={selskapsAvtale?.avtale.målerId}
          />
          <DescriptionTerm
            tranlsationFeltKey="pages.avtaler.avtaler.detaljer.antallBoenheter"
            value={
              selskapsAvtale?.avtale.avtaleDetaljerSelskap?.antallBoenheter
            }
          />
          {selskapsAvtale?.avtale.avtaleDetaljerSelskap?.næringsarealProsent ? (
            <DescriptionTerm
              tranlsationFeltKey="pages.avtaler.avtaler.detaljer.næringsareal"
              value={formatPercentage(
                selskapsAvtale?.avtale.avtaleDetaljerSelskap
                  ?.næringsarealProsent,
              )}
            />
          ) : (
            <DescriptionTerm
              tranlsationFeltKey="pages.bekreftelse.avtale.selskap.informasjon-om-boligselskap.næringsdrivende"
              value={t("enums.jaNei_nei")}
            />
          )}
        </DescriptionList>
      </AvtaleSectionWithHeader>
      {selskapsAvtale?.avtale.avtaleStatus === "Avbestilt" ? (
        <AvtaleAvbestiltAlert avtale={selskapsAvtale.avtale} />
      ) : selskapsAvtale?.avtale.avtaleStatus === "Utgått" ? (
        <AvtaleUtloptAlert avtale={selskapsAvtale.avtale} />
      ) : selskapsAvtale?.avtale.avbestillingsFrist != null &&
        dateHasPassed(selskapsAvtale.avtale.avbestillingsFrist) ? (
        <AvbestillingsFristUtloptAlert avtale={selskapsAvtale.avtale} />
      ) : (
        <AvtaleSectionWithHeader
          title={t("pages.avtaler.avbestilling.bekreft-avbestilling-tittel")}
          className="space-y-4"
        >
          <Checkbox
            label={t(
              "pages.avtaler.avbestilling.bekreft-avbestilling-checkbox",
            )}
            checked={bekreftAvbestilling}
            onChange={() => setBekreftAvbestilling(!bekreftAvbestilling)}
          />

          {avbestillingBekreftet && <AvbestillBekreftelseManglerFeil />}
          {avbestillingFeilet && <AvbestillFeilet />}

          <div className="flex gap-6 mt-4">
            <Button variant="secondary" onClick={handleAvbryt}>
              {t("pages.avtaler.avbestilling.knapp-avbryt")}
            </Button>
            <Button
              variant="primary"
              data-color={"danger"}
              onClick={handleAvbestill}
              loading={postAvbestillMutation.isPending}
            >
              {t("pages.avtaler.avbestilling.knapp-avbestill")}
            </Button>
          </div>
        </AvtaleSectionWithHeader>
      )}
    </AvbestillDetaljerContainer>
  );
};
